import { ShiftType } from "@/components/ProjectSettings/ProjectShifts";
import TimeAutocomplete from "@/components/TimeAutocomplete";
import SettingsContext from "@/Contexts/Settings/Context";
import { objectLength } from "@/utils/helperFunctions";
import { Button, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { Grid } from "@mui/system";
import React, { useContext, useRef, useState } from "react";
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { debounce } from "lodash/fp";

const normalDebounce = debounce(500);

const WorkShiftsSection: React.FC = () => {
  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const { defaultWorkShifts, handleCompanySettingChange } = useContext(SettingsContext);
  const [shifts, setShifts] = useState<ShiftType[]>(() => JSON.parse(defaultWorkShifts || '[]'));

  const handleAddShift = () => {
    const newShift: ShiftType = {
      name: '',
      startTime: '09:00',
      endTime: '17:00'
    };
    setShifts([...shifts, newShift]);
  };

  const handleDeleteShift = (index: number) => {
    const updatedShifts = shifts.filter((_, i) => i !== index);

    setShifts(updatedShifts);

    handleCompanySettingChange('work-shifts', JSON.stringify(updatedShifts))
  };

  const handleShiftChange = (index: number, field: keyof ShiftType, value: string) => {
    const updatedShifts = [...shifts];
    updatedShifts[index] = { ...updatedShifts[index], [field]: (value || "") };
    setShifts(updatedShifts);

    debounceFunc.cancel();
    debounceFunc(() => {
      handleCompanySettingChange('work-shifts', JSON.stringify(updatedShifts))
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
          Available Shifts:
        </Typography>
        {objectLength(shifts) > 0 ?
          shifts.map((shift, index) => (
            <Grid container key={index} sx={{ mb: 2 }} alignItems="baseline" justifyContent='space-between' spacing={1}>
              <Grid size={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Shift Name"
                  value={shift.name}
                  onChange={(e) => handleShiftChange(index, "name", e.target.value)}
                  error={shift.name.trim() === ""}
                  helperText={shift.name.trim() === "" ? "Required" : ""}
                />
              </Grid>

              <Grid size={3}>
                <TimeAutocomplete
                  time={shift.startTime}
                  label="Start Time"
                  onTimeChange={(newValue) => handleShiftChange(index, 'startTime', newValue)}
                />
              </Grid>

              <Grid size={3}>
                <TimeAutocomplete
                  time={shift.endTime}
                  label="End Time"
                  onTimeChange={(newValue) => handleShiftChange(index, 'endTime', newValue)}
                />
              </Grid>

              <Grid size="auto">
                <IconButton
                  color="error"
                  onClick={() => handleDeleteShift(index)}
                  size="small"
                >
                  <Tooltip title="Delete Shift">
                    <DeleteIcon />
                  </Tooltip>
                </IconButton>
              </Grid>
            </Grid>
          )) :
          <Typography variant="body2" color="textSecondary" textAlign="center">Add new shift</Typography>
        }

      </Grid>
      <Grid container justifyContent="flex-end" size={12}>
        <Button
          color="primary"
          size="small"
          onClick={handleAddShift}
          variant="outlined"
          disabled={shifts.some(shift => shift.name.trim() === "")}
        >
          <Tooltip title="Add Shift">
            <AddIcon />
          </Tooltip>
        </Button>
      </Grid>
    </Grid>
  );
}

export default WorkShiftsSection;
